import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Braces, ClipboardCopy, Copy, Download, ListOrdered, Radio, Trash2, X } from 'lucide-react';
import { cn } from '../../poker-ui/utils/cn';
import {
  formatAuctionBid,
  formatBelotCard,
  formatBelotTrumpBid,
} from '../../game/belotArmenianCards';

/**
 * Полная клиентская отладка «Открытый белот 1×1 · онлайн»: публичный снимок + приватные данные героя + производные поля.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} props.onClearDebug
 * @param {Record<string, unknown>} props.report — см. `buildOpenDuellOnlineDebugReport` в BaseGameRoomScreen
 */
export function OpenDuellOnlineEngineDebugPanel({ open, onClose, onClearDebug, report }) {
  const [tab, setTab] = useState('summary');
  const [copyNotice, setCopyNotice] = useState(/** @type {null | 'full'} */ (null));

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const fullJson = useMemo(() => JSON.stringify(report ?? {}, null, 2), [report]);

  const handleCopyFull = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullJson);
      setCopyNotice('full');
      window.setTimeout(() => setCopyNotice((n) => (n === 'full' ? null : n)), 3200);
    } catch {
      setCopyNotice(null);
    }
  }, [fullJson]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([fullJson], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `open-duell-online-debug-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [fullJson]);

  const od = report?.belotOpenDuell;
  const odJson = useMemo(() => JSON.stringify(od ?? null, null, 2), [od]);
  const roomJson = useMemo(() => JSON.stringify(report?.roomSnapshot ?? {}, null, 2), [report?.roomSnapshot]);
  const cmdsJson = useMemo(() => JSON.stringify(report?.clientCommandsSent ?? [], null, 2), [report?.clientCommandsSent]);
  const timelineJson = useMemo(() => JSON.stringify(report?.belotSyncTimeline ?? [], null, 2), [report?.belotSyncTimeline]);

  const summaryText = typeof report?.humanSummary === 'string' ? report.humanSummary : '';

  if (!open) return null;

  const tabs = [
    { id: 'summary', label: 'Сводка', icon: Braces },
    { id: 'openDuell', label: 'belotOpenDuell', icon: Copy },
    { id: 'room', label: 'Комната', icon: Radio },
    { id: 'commands', label: 'Клиент→сервер', icon: ListOrdered },
    { id: 'timeline', label: 'Лента sync', icon: ListOrdered },
  ];

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[240] bg-black/60 backdrop-blur-[1px]"
        aria-label="Закрыть отладку"
        onClick={onClose}
      />
      <div
        className={cn(
          'fixed z-[241] flex max-h-[min(92vh,760px)] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-border/60 bg-card text-foreground shadow-[0_16px_48px_rgba(0,0,0,0.5)]',
          'inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="open-duell-debug-title"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border/50 px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Braces className="h-5 w-5 shrink-0 text-violet-400/90" aria-hidden />
            <div className="min-w-0">
              <h2 id="open-duell-debug-title" className="truncate text-sm font-bold">
                Движок · открытый белот 1×1 онлайн
              </h2>
              <p className="truncate text-[10px] text-muted-foreground">
                Всё, что видит клиент: снимок, рука/стол героя, ход, дедлайны, легалы, команды, лента belotSync
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={handleCopyFull}
              title="Скопировать один JSON со всеми разделами"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-foreground transition hover:bg-muted/50"
            >
              <ClipboardCopy className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={handleDownload}
              title="Скачать тот же JSON"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-foreground transition hover:bg-muted/50"
            >
              <Download className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Очистить лог команд и ленту sync в памяти браузера?')) onClearDebug();
              }}
              title="Очистить лог"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-foreground transition hover:bg-muted/50"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-muted/30 transition hover:bg-muted/50"
              aria-label="Закрыть"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>

        {copyNotice === 'full' ? (
          <p className="border-b border-emerald-500/30 bg-emerald-950/30 px-3 py-1.5 text-center text-[11px] text-emerald-200">
            Полный JSON скопирован в буфер обмена
          </p>
        ) : null}

        <div className="border-b border-border/50 px-3 py-2.5 sm:px-4">
          <button
            type="button"
            onClick={handleCopyFull}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-violet-400/50 bg-gradient-to-b from-violet-900/55 via-violet-950/40 to-slate-950/50 py-3 text-xs font-bold uppercase tracking-wide text-violet-50 shadow-[0_8px_28px_rgba(139,92,246,0.2)] transition hover:border-violet-300/60 hover:from-violet-800/60 active:scale-[0.99] sm:text-[13px]"
          >
            <Copy className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
            Скопировать всю сводку одним кликом
          </button>
          <p className="mt-2 text-center text-[10px] leading-snug text-muted-foreground">
            Один файл: публичный <code className="text-foreground/90">belotOpenDuell</code>, приватная рука/стол, места,
            производные UI, дедлайны, легальные ходы, очередь команд, история sync.
          </p>
        </div>

        <div className="flex gap-1 border-b border-border/40 px-2 py-1.5 sm:px-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'flex flex-1 items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[10px] font-semibold transition sm:text-[11px]',
                tab === t.id
                  ? 'bg-violet-500/20 text-violet-100'
                  : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
              )}
            >
              <t.icon className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
              <span className="truncate">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2 sm:px-3 sm:py-3">
          {tab === 'summary' ? (
            <pre className="max-h-[min(62vh,560px)] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border/50 bg-black/40 p-2 font-mono text-[10px] leading-snug text-slate-100 sm:text-[11px]">
              {summaryText || '—'}
            </pre>
          ) : null}
          {tab === 'openDuell' ? (
            <pre className="max-h-[min(62vh,560px)] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border/50 bg-black/40 p-2 font-mono text-[10px] leading-snug text-slate-100 sm:text-[11px]">
              {odJson}
            </pre>
          ) : null}
          {tab === 'room' ? (
            <pre className="max-h-[min(62vh,560px)] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border/50 bg-black/40 p-2 font-mono text-[10px] leading-snug text-slate-100 sm:text-[11px]">
              {roomJson}
            </pre>
          ) : null}
          {tab === 'commands' ? (
            <pre className="max-h-[min(62vh,560px)] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border/50 bg-black/40 p-2 font-mono text-[10px] leading-snug text-slate-100 sm:text-[11px]">
              {cmdsJson}
            </pre>
          ) : null}
          {tab === 'timeline' ? (
            <pre className="max-h-[min(62vh,560px)] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border/50 bg-black/40 p-2 font-mono text-[10px] leading-snug text-slate-100 sm:text-[11px]">
              {timelineJson}
            </pre>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * Собирает человекочитаемую сводку и вложенные блоки для JSON (вызывать из родителя в useMemo).
 * @param {object} p
 */
export function buildOpenDuellOnlineDebugReport(p) {
  const lines = [];
  const {
    exportedAt = new Date().toISOString(),
    tableId,
    gameRoomId,
    roomSnapshot = {},
    publicStateParsed = null,
    belotVariant,
    belotOpenDuell = null,
    belotSync = null,
    belotSeats = {},
    belotSeatRows = [],
    myBelotSeat = null,
    privateHandIds = [],
    privateTableStacks = [],
    derived = {},
    belotTurnDeadlineAtMs = null,
    heroSecLeft = null,
    heroTurnSeconds = 30,
    openDuellLegalPlayCardIds = null,
    clientCommandsSent = [],
    belotSyncTimeline = [],
  } = p;

  const handIds = Array.isArray(privateHandIds) ? privateHandIds : [];
  const handLabels = handIds.map((id) => formatBelotCard(id));
  const stacks = Array.isArray(privateTableStacks) ? privateTableStacks : [];
  const tableStacksLabels = stacks.map((col) =>
    Array.isArray(col) ? col.map((id) => formatBelotCard(id)) : [],
  );
  const privateHero = {
    seat: myBelotSeat,
    handIds,
    handLabels,
    tableStacksLabels,
    tableStacksIds: stacks,
  };

  lines.push('=== Открытый белот 1×1 · онлайн · клиентская отладка ===');
  lines.push(`export: ${exportedAt}`);
  lines.push(`tableId: ${String(tableId ?? '')}  gameRoomId: ${String(gameRoomId ?? '')}`);
  lines.push(`roomId: ${roomSnapshot?.roomId ?? '—'}  handIndex: ${roomSnapshot?.handIndex ?? '—'}  updatedAt: ${roomSnapshot?.updatedAt ?? '—'}`);
  lines.push(`belotVariant: ${String(belotVariant ?? '')}`);
  lines.push('');

  lines.push('--- Места (belotSeats → строки) ---');
  for (const row of belotSeatRows || []) {
    lines.push(`  seat ${row.seat}: ${row.name}  uid:${String(row.uid || '').slice(0, 8)}…`);
  }
  lines.push('');

  lines.push('--- Герой (приватное с сервера) ---');
  lines.push(`  seat: ${privateHero.seat ?? '—'}`);
  if (Array.isArray(privateHero.handLabels)) {
    lines.push(`  рука (${privateHero.handLabels.length}): ${privateHero.handLabels.join(' ')}`);
    lines.push(`  рука id: ${JSON.stringify(privateHero.handIds ?? [])}`);
  }
  if (Array.isArray(privateHero.tableStacksLabels)) {
    lines.push('  стол (5 колонок, снизу→верх):');
    privateHero.tableStacksLabels.forEach((col, i) => {
      lines.push(`    [${i}] ${Array.isArray(col) ? col.join(' | ') : '—'}`);
    });
  }
  lines.push('');

  lines.push('--- Производные UI ---');
  lines.push(`  phase: ${derived.phase ?? '—'}`);
  lines.push(`  currentTurnSeat: ${derived.belotTurnSeat ?? '—'}  (мой ход аукциона: ${derived.myBelotTurnAuction})  (мой ход взятки: ${derived.myBelotTurnPlay})`);
  lines.push(`  interactionBlocked: ${derived.belotOnlineInteractionBlocked}  pendingCommands: ${JSON.stringify(derived.belotPendingCommands ?? {})}`);
  if (derived.openDuellLegalComputeError) {
    lines.push(`  openDuellLegalComputeError: ${derived.openDuellLegalComputeError}`);
  }
  lines.push(`  belotTurnDeadlineAtMs: ${belotTurnDeadlineAtMs ?? '—'}  heroSecLeft: ${heroSecLeft ?? '—'} / ${heroTurnSeconds}s`);
  const phaseUi = String(derived.phase ?? '');
  const leadRaw = belotOpenDuell?.trickLeadSeat;
  /** @type {string[]} */
  const legalNullBits = [];
  if (phaseUi !== 'play') legalNullBits.push(`фаза «${phaseUi || '—'}» (расчёт legal только в play)`);
  if (!belotOpenDuell?.contract) legalNullBits.push('нет contract в belotOpenDuell');
  if (phaseUi === 'play' && leadRaw == null) legalNullBits.push('trickLeadSeat=null — legal не считается (баг снапшота?)');
  if (privateHero.seat == null) legalNullBits.push('герой seat=null (не за столом)');
  if (!derived.myBelotTurnPlay) legalNullBits.push('myBelotTurnPlay=false (не ваш ход по currentTurnSeat)');
  if (derived.belotOnlineInteractionBlocked) legalNullBits.push('belotOnlineInteractionBlocked=true');
  const legalNullReason = legalNullBits.length ? legalNullBits.join(' · ') : 'см. useMemo openDuellLegalPlayCardIds в BaseGameRoomScreen';

  if (openDuellLegalPlayCardIds != null) {
    const ids = openDuellLegalPlayCardIds;
    if (Array.isArray(ids) && ids.length === 0) {
      lines.push('  legalPlay ids: []');
      lines.push(
        '  legalPlay: (пусто) — движок+фильтр дали 0 карт; при ходе со стола (seat+2) проверьте верхи колонок в привате и currentTurnSeat',
      );
    } else {
      const labels = Array.isArray(ids) ? ids.map((id) => formatBelotCard(id)) : [];
      lines.push(`  legalPlay ids: ${JSON.stringify(ids)}`);
      lines.push(`  legalPlay: ${labels.join(' ')}`);
    }
  } else if (derived.openDuellLegalComputeError) {
    lines.push(`  legalPlay: (null) — исключение при расчёте: ${derived.openDuellLegalComputeError}`);
  } else {
    lines.push(`  legalPlay: (null) — ${legalNullReason}`);
  }
  if (derived.openDuellBellaNotice) lines.push(`  bellaNotice: ${derived.openDuellBellaNotice}`);
  lines.push('');

  lines.push('--- Публичный стол (belotOpenDuell) кратко ---');
  if (belotOpenDuell && typeof belotOpenDuell === 'object') {
    lines.push(`  roundIndex: ${belotOpenDuell.roundIndex ?? '—'}`);
    lines.push(`  phase: ${belotOpenDuell.phase ?? '—'}`);
    lines.push(`  currentTurnSeat: ${belotOpenDuell.currentTurnSeat ?? '—'}`);
    lines.push(`  dealerSeat: ${belotOpenDuell.dealerSeat ?? '—'}`);
    lines.push(`  teamScores: ${JSON.stringify(belotOpenDuell.teamScores ?? null)}`);
    lines.push(`  handCounts: ${JSON.stringify(belotOpenDuell.handCounts ?? null)}`);
    lines.push(`  tricksTakenByTeam: ${JSON.stringify(belotOpenDuell.tricksTakenByTeam ?? null)}`);
    lines.push(`  trickIndex: ${belotOpenDuell.trickIndex ?? '—'}`);
    const tcp = belotOpenDuell.tableColumnsPublic;
    if (Array.isArray(tcp) && tcp.length) {
      lines.push('  tableColumnsPublic (видимые столбцы соперника/героя по логическим местам):');
      for (let pi = 0; pi < tcp.length; pi += 1) {
        const cols = tcp[pi];
        if (!Array.isArray(cols)) continue;
        const bits = cols.map((col, ci) => {
          const top = col?.topCard;
          const topL = top != null && top !== 'hidden' ? formatBelotCard(top) : String(top ?? '—');
          return `[${ci}] depth=${col?.depth ?? 0} top=${topL} bottom=${col?.bottomCard === 'hidden' ? 'hidden' : col?.bottomCard != null ? formatBelotCard(col.bottomCard) : '—'}`;
        });
        lines.push(`    игрок ${pi}: ${bits.join(' · ')}`);
      }
    }
    const lct = belotOpenDuell.lastCompletedTrick;
    if (lct && typeof lct === 'object') {
      lines.push(
        `  lastCompletedTrick: #${lct.trickIndex ?? '—'} winnerSeat=${lct.winnerSeat ?? '—'} plays=${Array.isArray(lct.plays) ? lct.plays.map((p) => `s${p?.seat}:${formatBelotCard(p?.card)}`).join(' ') : '—'}`,
      );
    }
    const auc = belotOpenDuell.auction;
    if (auc && typeof auc === 'object') {
      const hb = auc.highestBid;
      lines.push(
        `  auction: phase=${auc.phase ?? '—'} mult=${auc.contraMultiplier ?? 1} bidAuthorSeat=${auc.bidAuthorSeat ?? '—'} highest=${hb ? formatAuctionBid(hb) : '—'}`,
      );
    }
    const c = belotOpenDuell.contract;
    if (c && typeof c === 'object') {
      lines.push(
        `  contract: team=${c.contractTeam ?? '—'} target=${c.contractTargetPoints ?? '—'} trump=${formatBelotTrumpBid(c.trumpSuit)} kapot=${Boolean(c.kapot)} mult=${c.contraMultiplier ?? 1}`,
      );
    }
    const trick = belotOpenDuell.currentTrick;
    if (Array.isArray(trick) && trick.length) {
      lines.push(
        `  currentTrick (${trick.length}): ${trick
          .map((pl) => `seat${pl?.seat}=${formatBelotCard(pl?.card)}`)
          .join(' · ')}`,
      );
    } else {
      lines.push('  currentTrick: []');
    }
    lines.push(
      `  trickLeadSeat: ${belotOpenDuell.trickLeadSeat ?? '—'}  (всегда в сводке; при null в play — клиент не сможет посчитать legal)`,
    );
    const ev = belotOpenDuell.lastEvents;
    if (Array.isArray(ev) && ev.length) {
      lines.push(`  lastEvents (последние 12 типов): ${ev
        .slice(-12)
        .map((e) => e?.type || '?')
        .join(' → ')}`);
    }
  } else {
    lines.push('  (null)');
  }
  lines.push('');

  lines.push('--- publicState (парсинг) ---');
  lines.push(JSON.stringify(publicStateParsed, null, 2));
  lines.push('');

  lines.push(`--- Лог команд клиента (последние ${clientCommandsSent.length}) ---`);
  lines.push('(полный массив на вкладке «Клиент→сервер» и в JSON)');
  lines.push('');

  lines.push(`--- Лента belotSync (записей: ${belotSyncTimeline.length}) ---`);
  lines.push(`текущий belotSync: ${JSON.stringify(belotSync)}`);
  lines.push('');
  lines.push('=== конец сводки · полные объекты — вкладки и «скопировать всё» ===');

  const humanSummary = lines.join('\n');

  let belotOpenDuellClone = belotOpenDuell;
  try {
    belotOpenDuellClone = JSON.parse(JSON.stringify(belotOpenDuell));
  } catch {
    /* оставляем ссылку */
  }

  return {
    reportVersion: 2,
    source: 'OpenDuellOnlineEngineDebugPanel',
    exportedAt,
    humanSummary,
    tableId: tableId ?? null,
    gameRoomId: gameRoomId ?? null,
    roomSnapshot,
    publicStateParsed,
    belotVariant: belotVariant ?? null,
    belotOpenDuell: belotOpenDuellClone,
    belotSync,
    belotSeats,
    belotSeatRows,
    privateHero,
    derived,
    belotTurnDeadlineAtMs,
    heroSecLeft,
    heroTurnSeconds,
    openDuellLegalPlayCardIds,
    openDuellLegalComputeError: derived.openDuellLegalComputeError ?? null,
    clientCommandsSent,
    belotSyncTimeline,
  };
}
